import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { ChevronDown, Check } from "lucide-react";

export default function BreedAutocomplete({ 
  value, 
  onChange, 
  breeds = [], 
  placeholder = "Seleziona o scrivi la razza",
  disabled = false 
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [filteredBreeds, setFilteredBreeds] = useState(breeds);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    setFilteredBreeds(breeds);
  }, [breeds]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
    
    if (val.trim() === "") {
      setFilteredBreeds(breeds);
    } else {
      const filtered = breeds.filter(breed => 
        breed.toLowerCase().includes(val.toLowerCase())
      );
      // Always include "Altro" if not already filtered
      if (!filtered.includes("Altro") && breeds.includes("Altro")) {
        filtered.push("Altro");
      }
      setFilteredBreeds(filtered);
    }
    setOpen(true);
  };

  const handleSelect = (breed) => {
    setInputValue(breed);
    onChange(breed);
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleFocus = () => {
    setOpen(true);
    setFilteredBreeds(breeds);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className="rounded-xl bg-[#F5F5F7] border-0 focus:ring-2 focus:ring-[#FF6B4A]/20 pr-10 h-12 text-base"
          style={{ fontSize: '16px' }}
          data-testid="breed-autocomplete-input"
        />
        <button
          type="button"
          onClick={() => !disabled && setOpen(!open)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7D7F8C] hover:text-[#3D405B] transition-colors"
          tabIndex={-1}
        >
          <ChevronDown 
            size={18} 
            className={`transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      
      {open && filteredBreeds.length > 0 && (
        <div 
          className="absolute z-[100] w-full mt-1 bg-white rounded-xl border border-black/10 shadow-xl max-h-60 overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
        >
          {filteredBreeds.slice(0, 20).map((breed, index) => (
            <button
              key={breed + index}
              type="button"
              onClick={() => handleSelect(breed)}
              className={`w-full px-4 py-3 text-left text-base hover:bg-[#F5F5F7] active:bg-[#EBEBEB] transition-colors flex items-center justify-between ${
                breed === inputValue ? 'bg-[#FF6B4A]/10 text-[#FF6B4A]' : 'text-[#1D1D1F]'
              } ${index === 0 ? 'rounded-t-xl' : ''} ${index === filteredBreeds.length - 1 ? 'rounded-b-xl' : ''}`}
              style={{ touchAction: 'manipulation' }}
              data-testid={`breed-option-${breed.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span>{breed}</span>
              {breed === inputValue && (
                <Check size={16} className="text-[#FF6B4A]" />
              )}
            </button>
          ))}
        </div>
      )}
      
      {open && filteredBreeds.length === 0 && inputValue.trim() !== "" && (
        <div className="absolute z-[100] w-full mt-1 bg-white rounded-xl border border-black/10 shadow-xl p-4">
          <p className="text-base text-[#86868B] text-center mb-2">
            Nessuna razza trovata
          </p>
          <button
            type="button"
            onClick={() => handleSelect(inputValue)}
            className="w-full px-4 py-2.5 text-base bg-[#F5F5F7] hover:bg-[#EBEBEB] active:bg-[#E0E0E0] rounded-xl text-[#1D1D1F] font-medium transition-colors"
            style={{ touchAction: 'manipulation' }}
          >
            Usa "{inputValue}" come razza
          </button>
        </div>
      )}
    </div>
  );
}
